import './index.css'
import api from '@disco/api/server'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { COLORS } from '@disco/utils/color-indicators'
import { convertRemToPx } from '@disco/utils/dom'
import { getNounForExt } from '@disco/utils/file-utils'
import captureError from '@disco/utils/captureError'
import isEmpty from 'lodash/isEmpty'
import kebabCase from 'lodash/kebabCase'
import lodashThrottle from 'lodash/throttle'
import * as React from 'react'
import { MdSettings } from '@react-icons/all-files/md/MdSettings'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import { useIsMounted } from 'usehooks-ts'
import { isAggregatedSearchTrack } from '@disco/utils/tracks'
import type { CustomFieldsConfig } from 'types/business'
import type { State } from 'types/redux'
import type { Comment, Track } from 'types/track'
import { makeIsFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getCustomFieldsConfig,
  getMultiLevelSetting,
} from '@disco/state-management/selectors/user'
import { newlines } from 'utils/dom'
import { getTrackField } from 'utils/track-metadata'
import { Flex, Spinner } from 'components/ds'
import HoverCard from 'components/ui/hover-card'
import Icon from 'components/ui/icon'
import CustomScrollbars from 'components/ui/scrollbar'
const maxHeight = convertRemToPx(17)
const attributeTransforms = {
  Length: 'duration',
  Formats: 'formats-text',
  Comments: 'comments',
}

const getAttribute = (prop: string) => {
  // TODO: remove custom css system
  const attribute = attributeTransforms[prop] || prop
  const resultAttribute = kebabCase(attribute)
  return `data-meta-${resultAttribute}`
}

type OwnProps = {
  track: Track
  customEditMetadataUrl?: string | null | undefined
  showViewLink?: boolean
  metadataSubset: string[]
  returnOnlyContent?: boolean
  onViewFullInfo?: () => void
  iconClassName?: string
  trigger?: 'hover' | 'click'
}

function mapStateToProps(state: State, props: OwnProps): any {
  const isFeatureEnabled = makeIsFeatureEnabled()
  let metadataSubset

  if (props.metadataSubset) {
    // for public pages
    metadataSubset = props.metadataSubset
  } else {
    // for in-app pages
    metadataSubset = getMultiLevelSetting.explorer_trackhover_fields(
      state.user,
    ).settingUser
  }

  return {
    colorIndicatorsEnabled: isFeatureEnabled(state, {
      featureName: COLOR_INDICATORS,
    }),
    customFieldsConfig: getCustomFieldsConfig(state),
    metadataSubset: metadataSubset,
  }
}

const TrackTooltipContent = ({
  showViewLink,
  track,
  customEditMetadataUrl,
  metadataSubset,
  colorIndicatorsEnabled,
  customFieldsConfig,
  fetchMetadata,
  onViewFullInfo,
}: {
  showViewLink: boolean | null | undefined
  track: Track
  customEditMetadataUrl: string | null | undefined
  metadataSubset: string[]
  colorIndicatorsEnabled: boolean
  customFieldsConfig: CustomFieldsConfig
  fetchMetadata: (track: Track) => Promise<Track>
  onViewFullInfo: (() => void) | null | undefined
}) => {
  const [trackWithMetadata, setTrackWithMetadata] = React.useState<
    Track | null | undefined
  >(null)
  const isMounted = useIsMounted()
  // TODO: this might not be needed in most cases as server returns tracks with metadata
  // in most if not all cases.
  // Before fetching, need to check if metadata subset is already present, if so,
  // no point to fetch it again unless we want to ensure that user sees the latest
  React.useEffect(() => {
    if (!isMounted) return

    const updateTrackWithMetadata = async () => {
      const trackWithMetadata = await fetchMetadata(track)
      if (isMounted()) setTrackWithMetadata(trackWithMetadata)
    }

    updateTrackWithMetadata()
  }, [track, isMounted, fetchMetadata])
  // track may arrive without metadata (e.g. a stub built from a non-Track source);
  // in that case wait for the fetch instead of flashing an empty state
  const isLoading = trackWithMetadata === null && isEmpty(track.metadata)
  const resolvedTrack = trackWithMetadata ?? track

  const viewFullText = `View full ${getNounForExt(resolvedTrack.ext)} info`

  return (
    <>
      {showViewLink && (
        <Link to="/settings/display" className="TrackTooltip_Setup">
          <MdSettings size={24} />
        </Link>
      )}
      <div className="TrackTooltip_Cont">
        <div className="TrackTooltip_Meta">
          <CustomScrollbars color="dark" autoHeight autoHeightMax={maxHeight}>
            {isLoading ? (
              <Flex justify="center" py="4">
                <Spinner className="TrackTooltip_Spinner" />
              </Flex>
            ) : (
              <TrackContents
                track={resolvedTrack}
                metadataSubset={metadataSubset}
                colorIndicatorsEnabled={colorIndicatorsEnabled}
                customFieldsConfig={customFieldsConfig}
              />
            )}
          </CustomScrollbars>
        </div>
        <div className="TrackTooltip_OverlayCont">
          <div className="TrackTooltip_OverlayBg" />
        </div>
        {!isLoading && showViewLink && onViewFullInfo && (
          <button
            type="button"
            className="TrackTooltip_MoreMetadata"
            onClick={onViewFullInfo}
          >
            {viewFullText}
          </button>
        )}
        {!isLoading && showViewLink && !onViewFullInfo && (
          <Link
            className="TrackTooltip_MoreMetadata"
            to={
              customEditMetadataUrl ||
              `/explorer/tracks/${track.id}/edit-metadata`
            }
          >
            {viewFullText}
          </Link>
        )}
      </div>
    </>
  )
}

const TrackMeta = ({
  prop,
  track,
  customFieldsConfig,
}: {
  prop: string
  track: Track
  customFieldsConfig: CustomFieldsConfig
}) => {
  const _getPropValue = (prop: string, track: Track) => {
    return getTrackField(
      { ...track, metadata: track.metadata, user: track.user },
      prop,
      customFieldsConfig,
    )
  }

  if (prop === 'Client comments' || prop === 'Internal comments') {
    let visibilityFilter
    if (prop === 'Client comments') visibilityFilter = 'client'
    if (prop === 'Internal comments') visibilityFilter = 'internal'
    const clientComments = track.comments
    const rows =
      clientComments &&
      clientComments
        .filter(c => c.visibility === visibilityFilter)
        .map((comment, index) => (
          <TrackComment key={index} comment={comment} commentIndex={index} />
        ))
    if (!rows || rows?.length === 0) return null
    return rows
  }

  let stringValue = _getPropValue(prop, track)

  const renderCommentsContent = value => {
    if (typeof value === 'string') {
      const formatString = newlines(value)
      return formatString || value
    } else {
      return value
    }
  }

  if (!stringValue) return null
  const attributes = {}
  attributes[getAttribute(prop)] = true
  return (
    <tr key={prop} {...attributes}>
      <td className="TrackTooltip_Key">{prop}</td>
      <td>
        {prop === 'Comments' ? renderCommentsContent(stringValue) : stringValue}
      </td>
    </tr>
  )
}

const TrackComment = ({
  comment,
  commentIndex,
}: {
  comment: Comment
  commentIndex: number
}) => {
  let commentMark = {}

  if (/standout/i.test(comment.comment) && comment.visibility === 'internal') {
    commentMark = {
      'data-meta-comment-standout': true,
    }
  }

  return (
    <tr key={commentIndex} {...commentMark}>
      <td className="TrackTooltip_Key">
        {comment.user.first_name} ({comment.visibility})
      </td>
      <td>{comment.comment}</td>
    </tr>
  )
}

const fetchTrackMetadata = async (track: Track) => {
  // can't dispatch() an action - this is a hot zone
  // TODO: we have to update track everytime track info is fetched,
  // in this case too, but I leave it for later as it's fetched for local
  // rendering purposes
  try {
    if (isAggregatedSearchTrack(track)) {
      return await api.getAggregatedTrackByUrl(track.url)
    }

    return await api.getTrack(track.id)
  } catch (e) {
    captureError(e, { description: 'Failed to fetch track metadata' })
  }

  return track
}

const TrackContents = ({
  track,
  metadataSubset,
  colorIndicatorsEnabled,
  customFieldsConfig,
}: {
  track: Track
  metadataSubset: string[]
  colorIndicatorsEnabled: boolean
  customFieldsConfig: CustomFieldsConfig
}) => {
  // const fields = !metadataSubset ? (
  //   <tbody>
  //     {defaultTooltipMetadata.map(key => this._renderMeta(key))}
  //     {customFields.map(key => this._renderMeta(key))}
  //     {/* {clientComments && clientComments.length ? ( */}
  //     {/*   <hr className="TrackTooltip_Divider" /> */}
  //     {/* ) : null} */}
  //     {clientComments && clientComments.map(this._renderComment)}
  //   </tbody>
  // ) : (
  //
  // )
  const fields = metadataSubset.map((key, index) => (
    <TrackMeta
      key={index}
      prop={key}
      track={track}
      customFieldsConfig={customFieldsConfig}
    />
  ))

  if (!fields.some(t => t)) {
    return (
      <div className="TrackTooltip_InnerMeta">
        There is no additional metadata to display for this file.
      </div>
    )
  }

  const keyTags = (track.native_tags ?? []).filter(tag => tag.color).slice(-3)
  return (
    <>
      <table className="TrackTooltip_InnerMeta">
        <tbody>{fields}</tbody>
      </table>
      {colorIndicatorsEnabled && keyTags.length > 0 && (
        <div className="TrackTooltip_KeyTags">
          <div className="TrackTooltip_KeyTags_Label">Key tags</div>
          {keyTags.map(tag => (
            <span
              className="TrackTooltip_KeyTags_Item"
              key={tag.id}
              style={{
                background: tag.color ? COLORS[tag.color] : undefined,
              }}
            >
              {tag.name}
            </span>
          ))}
        </div>
      )}
    </>
  )
}

const TrackTooltip = ({
  showViewLink,
  track,
  returnOnlyContent,
  colorIndicatorsEnabled,
  metadataSubset,
  customFieldsConfig,
  customEditMetadataUrl,
  onViewFullInfo,
  iconClassName,
  trigger,
}: OwnProps & {
  colorIndicatorsEnabled: boolean
  customFieldsConfig: CustomFieldsConfig
  metadataSubset: string[]
}) => {
  const fetchMetadata = React.useRef(
    lodashThrottle(track => fetchTrackMetadata(track), 5000),
  ).current
  if (!track.uploaded) return null
  if (track.transcode_pending) return null

  if (returnOnlyContent) {
    // TODO: make public page use this component directly
    return (
      <TrackContents
        track={track}
        colorIndicatorsEnabled={colorIndicatorsEnabled}
        metadataSubset={metadataSubset}
        customFieldsConfig={customFieldsConfig}
      />
    )
  }

  return (
    <HoverCard
      contentClassName="TrackTooltip_HoverCard_Content"
      pointer="none"
      position="bottom"
      popoverClassname="TrackTooltip_Popover"
      trigger={trigger}
      flipStrategy="space"
      content={
        <TrackTooltipContent
          showViewLink={showViewLink}
          customEditMetadataUrl={customEditMetadataUrl}
          track={track}
          metadataSubset={metadataSubset}
          colorIndicatorsEnabled={colorIndicatorsEnabled}
          customFieldsConfig={customFieldsConfig}
          fetchMetadata={fetchMetadata}
          onViewFullInfo={onViewFullInfo}
        />
      }
      activator={
        <button
          type="button"
          className={
            trigger === 'click'
              ? 'TrackTooltip_Toggle TrackTooltip_Toggle--clickable'
              : 'TrackTooltip_Toggle'
          }
        >
          <Icon type="info-outline" className={iconClassName} />
        </button>
      }
    />
  )
}

export default connect(mapStateToProps)(TrackTooltip) as any
